import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { DictadoPayload, DictadoRuntime } from '../../core/types/payloads/dictado.types';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import {
  normalizeRhythmPattern,
  normalizeTokenArray,
  patternToTeacherScript,
  tokenToLabel,
  tokenToSymbol,
} from '../../core/music/tokens';
import { DEFAULT_MUSIC_PREFS } from '../../core/types/music';

export function validateDictadoPayload(raw: unknown): DictadoPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Dictado payload must be an object');
  const data = raw as Partial<DictadoPayload>;
  if (!data.title) throw new Error('Dictado requires title');

  const rhythmPattern = normalizeRhythmPattern(data.rhythmPattern);
  const patternTokens =
    rhythmPattern?.pattern ??
    normalizeTokenArray(data.patternTokens ?? data.patternSyllables ?? data.patternSymbols);
  if (patternTokens.length < 1) throw new Error('Dictado requires patternTokens');

  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly'
      ? data.rhythmMethod
      : 'tradicional';
  const graphicMode = data.graphicMode === 'symbols-labels' ? 'symbols-labels' : 'symbols';
  const prefs = { readingMethod: rhythmMethod, graphicMode };

  const patternSymbols = patternTokens.map(tokenToSymbol);
  const patternSyllables = patternTokens.map((t) => tokenToLabel(t, rhythmMethod));

  let teacherScript = String(data.teacherScript || '').trim();
  if (!teacherScript) teacherScript = patternToTeacherScript(patternTokens, prefs);

  const studentPrompt = String(
    data.studentPrompt || 'Transcribe el patrón rítmico usando símbolos o nombres de figuras:',
  ).trim();

  return {
    title: String(data.title),
    studentPrompt,
    teacherScript,
    patternTokens,
    patternSyllables,
    patternSymbols,
    rhythmPattern: rhythmPattern ?? {
      timeSignature: (data.meter as '4/4' | '3/4' | '2/4') || '4/4',
      pattern: patternTokens,
    },
    rhythmMethod,
    graphicMode,
    meter: data.meter ? String(data.meter) : '4/4',
    transcriptionLines: Number(data.transcriptionLines) || 4,
  };
}

export function createInitialDictadoRuntime(_payload: DictadoPayload, _routing: RoutingMode): DictadoRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const dictadoLogic: ActivityLogicPlugin<'dictado'> = {
  mode: 'dictado',
  validatePayload: validateDictadoPayload,
  createInitialRuntime: createInitialDictadoRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    if (input.action === 'FILL') {
      const value = String((input.data as { value?: string })?.value ?? '');
      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [participant.participantId]: value },
        },
      };
    }
    if (input.action === 'SUBMIT') {
      const finalized = { ...ctx.runtime.finalized, [participant.participantId]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: { submitted },
        },
      };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoDictadoPayload(): DictadoPayload {
  return validateDictadoPayload({
    title: 'Dictado rítmico',
    studentPrompt: 'Transcribe el patrón rítmico:',
    patternTokens: ['E', 'Q', 'E', 'Q'],
    meter: '4/4',
  });
}
